import Link from 'next/link'

const FaqLink = ({
  text,
  href,
  target,
}: {
  text: string
  href: string
  target?: string
}) => (
  <span className="z-10 text-blue hover:underline">
    <Link href={href} target={target}>
      {text}
    </Link>
  </span>
)

export const homePageQuestions = [
  {
    title: 'What is KNXT?',
    answer: (
      <p>
        KNXT is a luxury wholesaler, an online shop powered by cutting-edge
        technologies, that sells high-end products from prestigious Houses.{' '}
      </p>
    ),
  },
  {
    title: 'What products are available on KNXT boutique?',
    answer: (
      <p>
        The products available on <FaqLink href="/" text="KNXT boutique" /> are
        luxury Ready to Wear, Shoes and Leather goods items for Men and Women.
        KNXT products are guaranteed authentic, coming directly from prestigious
        Luxury Houses, including Gucci, Bottega Veneta, Alexander McQueen,
        Courrèges and Balenciaga.
      </p>
    ),
  },
  {
    title: 'What is a blockchain wallet and which wallets are accepted here?',
    answer: (
      <p>
        A blockchain wallet is a digital wallet that allows users to store and
        transfer cryptocurrencies. To store your digital assets safely, wallets
        like MetaMask provide you with a wallet address, key vault, secure
        login, and backup options.
        <br />
        <br />
        Metamask and any wallet supported by WalletConnect can be used to
        connect with to <FaqLink href="/" text="KNXT boutique" /> . Torus
        wallets are also compatible with{' '}
        <FaqLink href="/" text="KNXT boutique" /> and can be generated using
        your email on social media. More wallets will be supported soon.
      </p>
    ),
  },
  {
    title:
      'Which countries do you ship to and what is the expected shipping window?',
    answer: (
      <p>
        We currently only ship to the US, except for Hawaii and Alaska. Once you
        receive your order confirmation by email, please wait up to 3 business
        days for shipment. Once your order is shipped and you receive the
        tracking number from FedEx, the delivery can take up to 2 business days.
        If you still have not received your order after these steps, please
        contact our <FaqLink href="/contact" text="customer support" />.
      </p>
    ),
  },
  {
    title: 'What is your return policy?',
    answer: (
      <p>
        You can request a return of any item purchased on{' '}
        <FaqLink href="/" text="KNXT boutique" /> within 7 days upon delivery.
        To do so, please contact our{' '}
        <FaqLink href="/contact" text="customer support" /> to receive the
        return authorization and instructions. Items must remain in their
        original condition with all labels attached and intact. You will have 7
        days to return your product(s) once you receive the return label. Please
        note that all returns must be made from the US.
      </p>
    ),
  },
] as const

export const contactPageQuestions = [
  {
    title: 'Can I cancel or modify my order?',
    answer: (
      <p>
        Orders can be canceled until they have been shipped. If your order has
        already been shipped, you can return it once you receive it. To do so,
        please contact our{' '}
        <FaqLink href="mailto:contact@knxt.shop" text="customer support" />.
      </p>
    ),
  },
  {
    title:
      'What should I do if I have not received my order confirmation email?',
    answer: (
      <p>
        If you paid with crypto: you will first receive a pre-confirmation email
        shortly after your payment. You will then receive a confirmation email
        once your transaction has been added to a block. If you don’t receive an
        email, please contact our customer support.
        <br />
        <br />
        If you paid with credit card: you will then receive a confirmation email
        once your transaction has been confirmed. If you don’t receive an email,
        please contact our{' '}
        <FaqLink href="mailto:contact@knxt.shop" text="customer support" />.
      </p>
    ),
  },
  {
    title: 'What is your return policy?',
    answer: (
      <p>
        You can request a return of any item purchased on{' '}
        <FaqLink href="/" text="KNXT boutique" /> within 7 days upon delivery.
        To do so, please contact our{' '}
        <FaqLink href="mailto:contact@knxt.shop" text="customer support" /> to
        receive the return authorization and instructions. Items must remain in
        their original condition with all labels attached and intact. You will
        have 7 days to return your product(s) once you receive the return label.
        Please note that all returns must be made from the US.
      </p>
    ),
  },
  {
    title: 'What can I do if my parcel is damaged?',
    answer: (
      <>
        <p>
          If your parcel is damaged, we strongly recommend that you decline your
          delivery and notify FedEx.
        </p>
        <p>
          If your products are damaged, please contact our{' '}
          <FaqLink href="mailto:contact@knxt.shop" text="customer support" />.
        </p>
      </>
    ),
  },
  {
    title: 'Am I eligible to get a KNXT NFT?',
    answer: (
      <p>
        Only the first 100 customers are eligible to get a KNXT NFT, from the
        Alpha Collection.{' '}
      </p>
    ),
  },
] as const

export const faqPageQuestions = [
  {
    sectionTitle: 'General',
    id: 'general',
    questions: [
      {
        title: 'What is KNXT?',
        answer: (
          <p>
            KNXT is a luxury wholesaler, an online shop powered by cutting-edge
            technologies, that sells high-end products from prestigious Houses.{' '}
          </p>
        ),
      },
    ],
  },
  {
    sectionTitle: 'Product Information',
    id: 'product-information',
    questions: [
      {
        title: 'What products are available on KNXT boutique?',
        answer: (
          <p>
            The products available on <FaqLink href="/" text="KNXT boutique" />{' '}
            are luxury Ready to Wear, Shoes and Leather goods items for Men and
            Women.{' '}
          </p>
        ),
      },
      {
        title: 'Where are the products sold on KNXT from?',
        answer: (
          <p>
            KNXT products are guaranteed authentic, coming directly from
            prestigious Luxury Houses, including Gucci, Bottega Veneta,
            Alexander McQueen, Courrèges and Balenciaga.{' '}
          </p>
        ),
      },
      {
        title: 'What if an item is sold out?',
        answer: (
          <p>
            If a product is sold out, we invite you to check availability at an
            ulterior time. Products are restocked on a regular basis.{' '}
          </p>
        ),
      },
      {
        title: 'How do I choose the right size for me?',
        answer: (
          <p>
            Size guides with specific recommendations are available on each
            product detailed page to help determine your best fit.{' '}
          </p>
        ),
      },
    ],
  },
  {
    sectionTitle: 'Connect',
    id: 'wallet',
    questions: [
      {
        title: 'What is a blockchain wallet?',
        answer: (
          <p>
            A blockchain wallet is a digital wallet that allows users to store
            and transfer cryptocurrencies. To store your digital assets safely,
            wallets like MetaMask provide you with a wallet address, key vault,
            secure login, and backup options.{' '}
          </p>
        ),
      },
      {
        title: 'How can I connect on KNXT?',
        answer: (
          <p>
            You can connect on KNXT with your email, with your social media, or
            with a blockchain Wallet directly. If you connected via email or
            social media, a Torus wallet will be created and linked
            automatically to your account.{' '}
          </p>
        ),
      },
      {
        title: 'What is a Torus wallet?',
        answer: (
          <p>
            A Torus wallet is a blockchain wallet associated to your account
            once you connect with your social media or email. For instance, the
            Torus wallet is supported by popular Web3 websites like{' '}
            <FaqLink
              href="https://rarible.com/connect"
              text="Rarible"
              target="blank"
            />
            .{' '}
          </p>
        ),
      },
      {
        title: 'What is the Torus wallet used for?',
        answer: (
          <p>
            An exclusive NFT is airdropped on the wallet you connected with
            whenever you make an order on KNXT boutique. This wallet can either
            be a Torus wallet associated to your social media or email, or an
            Ethereum wallet you directly connected with (e.g. MetaMask){' '}
          </p>
        ),
      },
      {
        title: 'How can I access my Torus wallet?',
        answer: (
          <p>
            To access your Torus wallet, you can use{' '}
            <FaqLink
              href="https://app.tor.us/"
              text="this link"
              target="blank"
            />{' '}
            and connect with the same authentication method (email or social)
            you used on KNXT boutique. For instance, if you used Google
            authentication on the website, you must use the same email on the
            Torus website. When you access the Torus website, make sure to use
            the same wallet generated on KNXT boutique.{' '}
          </p>
        ),
      },
      {
        title: 'Which wallets are accepted on KNXT?',
        answer: (
          <p>
            Metamask and any wallet supported by WalletConnect can be used to
            connect with to <FaqLink href="/" text="KNXT boutique" />. Torus
            wallets are also compatible with KNXT boutique and can be generated
            using your email on social media. More wallets will be supported
            soon.
          </p>
        ),
      },
      {
        title: 'Can I check out without connecting my wallet?',
        answer: <p>No, you must connect your wallet to check out. </p>,
      },
    ],
  },
  {
    sectionTitle: 'Orders',
    id: 'orders',
    questions: [
      {
        title: 'How do I place an order?',
        answer: (
          <p>
            Once you are connected with your wallet, click on ‘Shop now’. Browse
            and select the item(s) you wish to purchase, select your size, and
            add the item(s) to your cart. Once all the items are in your cart,
            enter your shipping information. Proceed to payment by clicking on
            "Pay with credit card" or "Pay with crypto" and follow the
            instructions.{' '}
          </p>
        ),
      },
      {
        title: 'Do I have an order limit?',
        answer: <p>You can have up to 3 items per order.</p>,
      },
      {
        title: 'Do I need to create an account to place an order?',
        answer: (
          <p>
            No, your wallet public address will act as your account credentials
            on <FaqLink href="/" text="KNXT boutique" />.
          </p>
        ),
      },
      {
        title: 'Can I cancel or modify my order?',
        answer: (
          <p>
            Orders can be canceled until they have been shipped. If your order
            has already been shipped, you can return it once you receive it. To
            do so, please contact our{' '}
            <FaqLink href="/contact" text="customer support" />.
          </p>
        ),
      },
      {
        title: 'Where can I check the status of my order?',
        answer: (
          <p>
            You can check your order status at any time in the ‘
            <FaqLink href="/orders" text="My Orders" />’ section.{' '}
          </p>
        ),
      },
      {
        title:
          'What should I do if I have not received my order confirmation email?',
        answer: (
          <p>
            If you paid with crypto: you will first receive a pre-confirmation
            email shortly after your payment. You will then receive a
            confirmation email once your transaction has been added to a block.
            If you don’t receive an email, please contact our customer support.
            <br />
            <br />
            If you paid with credit card: you will then receive a confirmation
            email once your transaction has been confirmed. If you don’t receive
            an email, please contact our{' '}
            <FaqLink href="/contact" text="customer support" />.
          </p>
        ),
      },
    ],
  },
  {
    sectionTitle: 'Shipping & delivery',
    id: 'shipping-and-delivery',
    questions: [
      {
        title: 'Are there shipping fees?',
        answer: <p>Shipping is free for all orders. </p>,
      },
      {
        title: 'What are the shipping methods?',
        answer: <p>We use FedEx Express Shipping.</p>,
      },
      {
        title: 'Which countries do you ship to?',
        answer: (
          <p>
            We currently only ship to the US, except for Hawaii and Alaska.{' '}
          </p>
        ),
      },
      {
        title: 'When will my order be shipped?',
        answer: (
          <p>
            Once your payment is validated (upon reception of your confirmation
            order email), our logistics team will proceed with the shipment of
            your order within 3 business days.{' '}
          </p>
        ),
      },
      {
        title: 'How can I track my order?',
        answer: (
          <p>
            Until shipment, your order status will be updated in the ‘My orders’
            section. Once your order is shipped, you will receive an email from
            FedEx with the dedicated link to track your delivery.{' '}
          </p>
        ),
      },
      {
        title: 'My order is validated but I need to change my shipping address',
        answer: (
          <p>
            We cannot guarantee that your shipping address will be updated, but
            please contact our{' '}
            <FaqLink href="/contact" text="customer support" />.
          </p>
        ),
      },
      {
        title: 'What is the expected shipping window?',
        answer: (
          <>
            <p>
              Once you receive your order confirmation by email, please wait up
              to 3 business days for shipment.
            </p>
            <p>
              Once your order is shipped and you receive the tracking number
              from FedEx, the delivery can take up to 2 business days. If you
              still have not received your order after these steps, please
              contact our <FaqLink href="/contact" text="customer support" />.
            </p>
          </>
        ),
      },
      {
        title: 'What can I do if my parcel is damaged?',
        answer: (
          <>
            <p>
              If your parcel is damaged, we strongly recommend that you decline
              your delivery and notify FedEx.{' '}
            </p>
            <p>
              If your products are damaged, please contact our{' '}
              <FaqLink href="/contact" text="customer support" />.
            </p>
          </>
        ),
      },
    ],
  },
  {
    sectionTitle: 'General Payment inquiries',
    id: 'payments',
    questions: [
      {
        title: 'What forms of payment do you accept?',
        answer: (
          <p>
            KNXT accepts payments in cryptocurrency with BitPay used as a
            Payment Service Provider (PSP), as well as with credit/debit cards.
          </p>
        ),
      },
      {
        title: 'What currencies can I use to pay?',
        answer: (
          <p>
            If you choose to pay by credit/debit card, KNXT supports payment in
            US Dollars.
            <br />
            If you choose to pay in crypto, KNXT supports the following
            currencies: Bitcoin (BTC), Ethereum (ETH), Litecoin (LTC), USD Coin
            (USDC), Dai (DAI), Binance USD (BUSD), Shiba Inu (SHIB), ApeCoin
            (APE), and Dogecoin (DOGE).
          </p>
        ),
      },
      {
        title: 'Are taxes included in the final price?',
        answer: (
          <p>
            Taxes are calculated at the checkout step after filling the shipping
            address.
          </p>
        ),
      },
      {
        title: 'Where can I find invoice and tax information?',
        answer: (
          <p>
            You will receive a shipment notification email along with your
            invoice and tax details once your order has been successfully
            shipped. You can also find your invoices in the ‘My orders’ section.
          </p>
        ),
      },
      {
        title: 'Do you accept gift cards?',
        answer: <p>We do not accept gift cards at the moment.</p>,
      },
    ],
  },
  {
    sectionTitle: 'Crypto payment',
    id: 'crypto-payments',
    questions: [
      {
        title: 'How do I pay with BitPay?',
        answer: (
          <>
            <p>
              After you enter your shipping details and validate your order
              summary, you are then able to click on ‘Pay with Bitpay’.
            </p>
            <p>
              You will be redirected to the Bitpay interface to choose the
              wallet you wish to pay from, choose your cryptocurrency and then
              proceed with payment within 15 minutes.
            </p>
          </>
        ),
      },
      {
        title: 'What is the role of Bitpay?',
        answer: (
          <p>
            BitPay operates just like any other payment method but for users who
            want to pay with cryptocurrencies. Learn more at{' '}
            <FaqLink href="https://bitpay.com" text="https://bitpay.com" />.
          </p>
        ),
      },
      {
        title:
          'Do I need to have a personal BitPay account to pay on KNXT boutique?',
        answer: (
          <>
            <p>
              For any order below $3,000, you can use BitPay transaction service
              without having your own BitPay account. BitPay will require an
              email address and use it only if they need to contact you about
              your payment, not for marketing purposes.
            </p>
            <p>
              For any order equivalent to or over $3,000, you will need to
              create a personal BitPay account to receive a BitPay ID. It is a
              US legal requirement, known as Know Your Customer (KYC). It takes
              about 5 minutes and is a one-time process. You do not have to use
              a BitPay wallet.
            </p>
          </>
        ),
      },
      {
        title: 'What fees will I pay?',
        answer: (
          <p>
            As for any transaction with crypto payment, you will have to pay for
            gas fees to the network validators. Keep in mind that fees can vary
            depending on network congestion.
          </p>
        ),
      },
      {
        title: 'When will my payment be confirmed?',
        answer: (
          <p>
            Your payment will be confirmed briefly after your crypto transaction
            is added to a block.{' '}
          </p>
        ),
      },
      {
        title: 'How long do I have to proceed with payment? ',
        answer: (
          <p>
            As soon as you selected your wallet and cryptocurrency in BitPay, a
            15-minute-timer is set, during which the duration of the total
            amount in crypto is frozen (based on a set FIAT to crypto conversion
            rate).{' '}
          </p>
        ),
      },
    ],
  },
  {
    sectionTitle: 'Credit card payment',
    id: 'credit-card-payments',
    questions: [
      {
        title: 'What payment methods do you accept?',
        answer: <p>KNXT supports payments in VISA, Mastercard and AMEX.</p>,
      },
      {
        title: 'How do I pay with credit card?',
        answer: (
          <p>
            After you enter your shipping details and validate your order
            summary, you are able to click on "Pay with credit card".
            <br />
            You can then enter your credit card details and click on the "Submit
            payment" button to be redirected to the payment validation page.
          </p>
        ),
      },
      {
        title: 'When will my payment be confirmed?',
        answer: (
          <p>
            Your payment will be confirmed briefly after the transaction has
            been accepted by your bank.{' '}
          </p>
        ),
      },
    ],
  },
  {
    sectionTitle: 'Returns and exchanges',
    id: 'returns-and-exchanges',
    questions: [
      {
        title: 'What is your return policy?',
        answer: (
          <p>
            You can request a return of any item purchased on{' '}
            <FaqLink href="/" text="KNXT boutique" /> within 7 days upon
            delivery. To do so, please contact our{' '}
            <FaqLink href="/contact" text="customer support" /> to receive the
            return authorization and instructions. Items must remain in their
            original condition with all labels attached and intact. You will
            have 7 days to return your product(s) once you receive the return
            label. Please note that all returns must be made from the US.{' '}
          </p>
        ),
      },
      {
        title: 'Where can I check the return status? ',
        answer: (
          <p>
            The status of your return can be tracked through FedEx website,
            thanks to the tracking number indicated on the return label.{' '}
          </p>
        ),
      },
      {
        title: 'Can I ask for an exchange?',
        answer: (
          <p>
            We currently do not accept exchange inquiries. If you wish to return
            your product, please contact our{' '}
            <FaqLink href="/contact" text="customer support" /> to initiate the
            return process.
          </p>
        ),
      },
      {
        title: 'When will I receive my refund?',
        answer: (
          <p>
            Once your return is completed (meaning it has been delivered to our
            warehouse and passed our quality check), a refund will be issued in
            the next 3 business days. For any questions related to refunds,
            please contact our{' '}
            <FaqLink href="/contact" text="customer support" />.
          </p>
        ),
      },
      {
        title: 'How will my refund be issued?',
        answer: (
          <p>
            {' '}
            You will be refunded once you successfully returned your product to
            KNXT.
            <br />
            <br />
            If you chose to pay by credit/debit card: your refund will be made
            in US Dollars, equal to the order amount.
            <br />
            <br />
            If you chose cryptocurrency payment: your refund will be made in the
            cryptocurrency you paid in US Dollars equivalent (adjusted to the
            current rate). You will receive an email from BitPay asking you to
            provide the public address on which you wish to be refunded.{' '}
          </p>
        ),
      },
    ],
  },
  {
    sectionTitle: 'NFT',
    id: 'nft',
    questions: [
      {
        title: 'What is an NFT?',
        answer: (
          <p>
            A Non-Fungible Token, or NFT, is a digital asset that represents
            ownership of a digital product. NFTs are cryptographic assets on a
            blockchain with unique identification codes and metadata that
            distinguish them from each other.{' '}
          </p>
        ),
      },
      {
        title: 'What are KNXT NFTs?',
        answer: (
          <p>
            You get an exclusive NFT for every order you make on KNXT. The first
            100 orders will get an NFT containing unique traits from ‘La
            Première’ collection.
          </p>
        ),
      },
      {
        title: 'When will I receive my NFT?',
        answer: (
          <p>
            Your NFT will be airdropped in your wallet 7 days after you receive
            your order, once the allowed return period is over. You will not
            receive your NFT if you choose to return your order.
          </p>
        ),
      },
      {
        title: 'How are KNXT NFTs created?',
        answer: (
          <p>
            Our NFTs are minted from our Smart Contract directly to the wallet
            you connected with to <FaqLink href="/" text="KNXT boutique" />.
          </p>
        ),
      },
      {
        title: 'Can an NFT be sold separately from the physical product?',
        answer: (
          <p>
            No, KNXT NFTs are neither sold nor given separately. They are
            airdropped upon purchase of any available product on KNXT. However,
            once airdropped NFTs can be listed on a marketplace independently
            from the physical product.{' '}
          </p>
        ),
      },
    ],
  },
] as const
